import React from 'react';
import './LoginScene.css';

/* ── Spinning truck wheel ─────────────────────────────────
   tyre ring (static) + rotating group (rim + spokes + hub)
   animateTransform type="rotate" spins around wheel centre
───────────────────────────────────────────────────────── */
function TruckWheel({ cx, cy, r = 18, dur = "1.4s" }) {
  const spokes = [0, 60, 120, 180, 240, 300].map((deg) => {
    const rad = (deg * Math.PI) / 180;
    return (
      <line
        key={deg}
        x1={cx + Math.cos(rad) * r * 0.22}
        y1={cy + Math.sin(rad) * r * 0.22}
        x2={cx + Math.cos(rad) * r * 0.52}
        y2={cy + Math.sin(rad) * r * 0.52}
        stroke="#8a92c0"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    );
  });
  return (
    <g>
      {/* outer tyre - static */}
      <circle cx={cx} cy={cy} r={r} fill="#1e2235" stroke="#2e3555" strokeWidth="2" />
      {/* inner rim + spokes + hub — rotates */}
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          from={`0 ${cx} ${cy}`}
          to={`360 ${cx} ${cy}`}
          dur={dur}
          repeatCount="indefinite"
        />
        <circle cx={cx} cy={cy} r={r * 0.55} fill="#3d4470" />
        {spokes}
        <circle cx={cx} cy={cy} r={r * 0.22} fill="#f0c060" />
      </g>
    </g>
  );
}

/* ── Twinkling star ─────────────────────────────────────── */
function Star({ cx, cy, r = 1.5, delay = 0 }) {
  return (
    <circle cx={cx} cy={cy} r={r} fill="#fff" opacity="0.85">
      <animate attributeName="opacity" values="0.85;0.15;0.85"
        dur="3s" begin={`${delay}s`} repeatCount="indefinite" />
    </circle>
  );
}

const STARS = [
  [55,35,1.2,0],[100,18,1.8,0.5],[160,52,1,1],[210,28,2,0.2],[275,14,1.5,0.8],
  [330,44,1,1.2],[390,22,1.8,0.4],[450,58,1.2,0.9],[510,32,2,0.1],[570,18,1.5,0.6],
  [630,48,1,1.4],[690,26,1.8,0.3],[750,40,1.2,0.7],[820,16,2,0.5],[880,36,1,1.1],
  [130,78,1.5,0.3],[360,72,1.2,0.9],[600,82,1.8,0.2],[770,68,1,0.6],[920,58,1.5,1.3],
  [35,65,1,0.7],[240,85,1.3,1.5],[480,75,1,0.4],[700,55,1.6,0.9],[850,80,1.2,0.2],
];

export default function LoginScene() {
  return (
    <svg
      className="login-scene-svg"
      viewBox="0 0 960 600"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#05091a" />
          <stop offset="50%" stopColor="#0d1535" />
          <stop offset="100%" stopColor="#18224a" />
        </linearGradient>

        {/* moon glow halo */}
        <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#fffbe8" stopOpacity="1" />
          <stop offset="50%" stopColor="#f5e090" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#f5e090" stopOpacity="0" />
        </radialGradient>

        <linearGradient id="hillFar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#18224a" />
          <stop offset="100%" stopColor="#0e1630" />
        </linearGradient>
        <linearGradient id="hillNear" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#121a38" />
          <stop offset="100%" stopColor="#0a1025" />
        </linearGradient>
        <linearGradient id="groundGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#0e1425" />
          <stop offset="100%" stopColor="#07091a" />
        </linearGradient>
        <linearGradient id="roadGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#1c2340" />
          <stop offset="100%" stopColor="#131830" />
        </linearGradient>

        {/* cab */}
        <linearGradient id="cabGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f0a030" />
          <stop offset="100%" stopColor="#c87820" />
        </linearGradient>
        {/* trailer */}
        <linearGradient id="trailGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f5f0e8" />
          <stop offset="100%" stopColor="#ddd5c5" />
        </linearGradient>
        {/* headlight beam */}
        <radialGradient id="beamGrad" cx="0%" cy="50%" r="100%">
          <stop offset="0%"   stopColor="#fff8cc" stopOpacity="0.65" />
          <stop offset="100%" stopColor="#fff8cc" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* ── Sky ── */}
      <rect width="960" height="600" fill="url(#skyGrad)" />

      {/* ── Stars ── */}
      {STARS.map(([x, y, r, d], i) => <Star key={i} cx={x} cy={y} r={r} delay={d} />)}

      {/* ── Moon (centred in scene so it never clips into form panel) ── */}
      {/* soft outer glow */}
      <circle cx="600" cy="105" r="100" fill="url(#moonGlow)" opacity="0.3">
        <animate attributeName="opacity" values="0.3;0.45;0.3" dur="6s" repeatCount="indefinite" />
      </circle>
      {/* moon disc */}
      <circle cx="600" cy="105" r="60" fill="#f5e88a" />
      {/* craters */}
      <circle cx="578" cy="90"  r="9"  fill="#e8d870" opacity="0.55" />
      <circle cx="618" cy="122" r="6"  fill="#e8d870" opacity="0.45" />
      <circle cx="598" cy="115" r="3.5" fill="#e8d870" opacity="0.4" />
      <circle cx="628" cy="92"  r="5"  fill="#e8d870" opacity="0.45" />
      <circle cx="582" cy="118" r="3"  fill="#e8d870" opacity="0.35" />

      {/* ── Far hills ── */}
      <path
        d="M0,415 Q70,355 155,385 Q240,355 335,395
           Q420,348 515,380 Q600,350 695,385
           Q785,355 875,390 Q920,368 960,380 L960,600 L0,600Z"
        fill="url(#hillFar)"
      />

      {/* ── Near hills ── */}
      <path
        d="M0,468 Q90,428 195,452 Q295,422 400,458
           Q475,432 575,458 Q670,432 775,458
           Q870,438 960,455 L960,600 L0,600Z"
        fill="url(#hillNear)"
      />

      {/* ── Ground ── */}
      <rect x="0" y="508" width="960" height="92" fill="url(#groundGrad)" />

      {/* ── Road ── */}
      <rect x="0" y="488" width="960" height="58" fill="url(#roadGrad)" />
      <line x1="0" y1="490" x2="960" y2="490" stroke="#252e58" strokeWidth="2" />
      <line x1="0" y1="544" x2="960" y2="544" stroke="#252e58" strokeWidth="2" />

      {/* ── Scrolling centre dashes ── */}
      <g>
        {[0,120,240,360,480,600,720,840,960].map((x) => (
          <rect key={x} x={x} y="516" width="65" height="7" rx="3.5" fill="#f0c060" opacity="0.55">
            <animateTransform attributeName="transform" type="translate"
              from="0,0" to="-120,0" dur="2.8s" repeatCount="indefinite" />
          </rect>
        ))}
      </g>

      {/* ── Moon shimmer on road ── */}
      <ellipse cx="600" cy="515" rx="45" ry="7" fill="#f5e88a" opacity="0.08" />

      {/* ════════════════════════════════════════════════════
          TRUCK — outer <g> drives left→right
          inner <g> bounces vertically
          additive="sum" ensures both compose correctly
         ════════════════════════════════════════════════════ */}
      <g>
        <animateTransform
          attributeName="transform"
          type="translate"
          from="-560,0"
          to="1000,0"
          dur="14s"
          repeatCount="indefinite"
        />

        <g>
          <animateTransform
            attributeName="transform"
            type="translate"
            values="0,0;0,-3;0,0;0,-1.5;0,0"
            dur="0.75s"
            repeatCount="indefinite"
            additive="sum"
          />

          {/* headlight beam cone */}
          <polygon
            points="445,468 445,502 610,508 610,462"
            fill="url(#beamGrad)"
            opacity="0.5"
          />

          {/* ── TRAILER ── */}
          <rect x="30"  y="385" width="372" height="112" rx="7" fill="url(#trailGrad)" />
          {/* highlight strip top */}
          <rect x="30"  y="385" width="372" height="9" rx="4" fill="#fff" opacity="0.35" />
          {/* vertical ribs */}
          {[102,172,242,312,366].map((x) => (
            <rect key={x} x={x} y="385" width="3" height="112" fill="#bbb3a3" opacity="0.5" />
          ))}
          {/* SOL TRANSPORT logo */}
          <text x="216" y="436"
            fontFamily="'Arial Black',Arial,sans-serif" fontWeight="900"
            fontSize="30" fill="#1a2140" textAnchor="middle" letterSpacing="3">SOL</text>
          <text x="216" y="462"
            fontFamily="'Arial Black',Arial,sans-serif" fontWeight="700"
            fontSize="13" fill="#1a2140" textAnchor="middle" letterSpacing="5">TRANSPORT</text>
          {/* reflector strip */}
          <rect x="36" y="482" width="360" height="7" rx="3.5" fill="#d83030" opacity="0.8" />
          {[36,136,236,336].map((x) => (
            <rect key={x} x={x} y="482" width="58" height="7" rx="3.5" fill="#f0c060" opacity="0.85" />
          ))}
          {/* tail lights */}
          <rect x="30" y="398" width="11" height="30" rx="3" fill="#e84040">
            <animate attributeName="opacity" values="1;0.4;1" dur="1.6s" repeatCount="indefinite" />
          </rect>
          <rect x="30" y="432" width="11" height="18" rx="3" fill="#f0c060">
            <animate attributeName="opacity" values="0.85;0.3;0.85" dur="1.6s" repeatCount="indefinite" />
          </rect>

          {/* ── HITCH ── */}
          <rect x="396" y="466" width="22" height="11" rx="3" fill="#30304a" />

          {/* ── CAB ── */}
          <rect x="408" y="388" width="128" height="110" rx="10" fill="url(#cabGrad)" />
          {/* roof fairing */}
          <rect x="422" y="374" width="82" height="20" rx="7" fill="#c07818" />
          {/* marker lights on roof */}
          {[432,450,468,486].map((x) => (
            <circle key={x} cx={x} cy="374" r="4.5" fill="#ffe060">
              <animate attributeName="opacity" values="1;0.3;1" dur="1.5s"
                begin={`${(x-432)/55}s`} repeatCount="indefinite" />
            </circle>
          ))}
          {/* windshield */}
          <rect x="420" y="394" width="78" height="52" rx="8" fill="#a5dff0" opacity="0.88" />
          <rect x="420" y="394" width="78" height="18" rx="8" fill="#7ccde8" opacity="0.55" />
          <rect x="420" y="394" width="78" height="52" rx="8" fill="none"
            stroke="#c07818" strokeWidth="3" />
          {/* A-pillar */}
          <line x1="460" y1="394" x2="460" y2="446" stroke="#c07818" strokeWidth="2.5" />
          {/* mirror arm + glass */}
          <rect x="501" y="406" width="24" height="15" rx="4" fill="#956015" />
          <rect x="504" y="408" width="18" height="11" rx="2" fill="#5ab0d5" opacity="0.7" />
          {/* door seam */}
          <line x1="460" y1="446" x2="460" y2="498" stroke="#c07818" strokeWidth="1.5" opacity="0.5" />
          <rect x="464" y="466" width="18" height="6" rx="3" fill="#956015" />
          {/* grille */}
          <rect x="412" y="458" width="98" height="38" rx="5" fill="#956015" />
          {[470,486,502].map((x) => (
            <line key={x} x1={x} y1="460" x2={x} y2="494" stroke="#c07818" strokeWidth="2.5" />
          ))}
          {[466,474,482,490].map((y) => (
            <line key={y} x1="414" y1={y} x2="508" y2={y} stroke="#c07818" strokeWidth="1.5" opacity="0.45" />
          ))}
          {/* bumper */}
          <rect x="408" y="494" width="128" height="10" rx="5" fill="#7a4010" />
          {/* headlight */}
          <rect x="506" y="460" width="26" height="18" rx="4" fill="#fff8cc">
            <animate attributeName="opacity" values="1;0.65;1" dur="2.2s" repeatCount="indefinite" />
          </rect>
          <rect x="506" y="460" width="26" height="18" rx="4" fill="none"
            stroke="#f0c060" strokeWidth="1.5" />
          {/* DRL strip */}
          <rect x="506" y="480" width="26" height="5" rx="2.5" fill="#fff8cc" opacity="0.55" />
          {/* exhaust stack */}
          <rect x="408" y="362" width="9" height="30" rx="4.5" fill="#505068" />
          {/* puff */}
          <circle cx="412" cy="358" r="6" fill="#8888a0" opacity="0">
            <animate attributeName="r"       values="3;12;3"      dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.4;0;0.4"   dur="2s" repeatCount="indefinite" />
            <animate attributeName="cy"      values="358;340;358" dur="2s" repeatCount="indefinite" />
          </circle>

          {/* ── WHEELS (all six spin) ── */}
          <TruckWheel cx={80}  cy={510} r={20} dur="1.4s" />
          <TruckWheel cx={130} cy={510} r={20} dur="1.4s" />
          <TruckWheel cx={270} cy={510} r={20} dur="1.4s" />
          <TruckWheel cx={320} cy={510} r={20} dur="1.4s" />
          <TruckWheel cx={430} cy={510} r={20} dur="1.4s" />
          <TruckWheel cx={500} cy={510} r={20} dur="1.4s" />

          {/* ground shadow */}
          <ellipse cx="285" cy="533" rx="245" ry="9" fill="#000" opacity="0.15" />
        </g>
      </g>

      {/* ── Tree silhouettes ── */}
      {[55,155,295,455,585,725,875].map((x, i) => (
        <g key={i}>
          <rect x={x-3} y={428} width="6" height={38+(i%3)*10} fill="#0c1428" opacity="0.75" />
          <circle cx={x} cy={422+(i%3)*5} r={13+(i%2)*7} fill="#0c1428" opacity="0.7" />
        </g>
      ))}
    </svg>
  );
}
